import AddIcon from '@/common/assets/icons/add.svg?react';
import RefreshIcon from '@/common/assets/icons/refreshIcon.svg?react';
import Button from '@/common/components/base/button/Button';
import DashedTitle from '@/common/components/base/dashedTitle/DashedTitle';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import ImageCard from '@/common/components/imageGallery/components/ImageCard';
import LoadingPage from '@/common/pages/LoadingPage';
import DamagePartEmptySpace from '@/modules/adjusterReportModules/IncidentParticipants/components/images/property/damaged/vehicleImages/vehicleDamageImagePreview/DamagePartEmptySpace';
import VehicleImagesEmptySpace from '@/modules/adjusterReportModules/IncidentParticipants/components/images/property/damaged/vehicleImages/vehicleDamageImagePreview/VehicleImagesEmptySpace copy';
import { useImageFlowStore } from '@/modules/adjusterReportModules/IncidentParticipants/store/imageFlow/useImageFlowStore';
import { UserTypeProps } from '@/modules/adjusterReportModules/IncidentParticipants/types/ParticipantType';
import SendOfflinePhotos from '@/modules/OfflineFiles/components/SendOfflinePhotos';
import useGetOfflineImages from '@/modules/OfflineFiles/hooks/useGetOfflineImages';
import { getHasOfflineShootingQueryOptions } from '@/modules/OfflineFiles/queries/getHasOfflineShooting/getHasOfflineShootingQueryOptions';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';
import { grid } from 'styled-system/patterns';
import { token } from 'styled-system/tokens';

type ImagesType = {
  name: string;
  src: string;
  id?: number;
};
const PreviewImages = ({
  retakeImage,
  addDamagePoint,
  images,
  handleDeleteImage,
  userType,
}: {
  retakeImage?: () => void;
  addDamagePoint?: () => void;
  images: { damagedImages: ImagesType[]; vehicleImages: ImagesType[] };
  handleDeleteImage?: (id: number, closeModal?: () => void) => void;
} & UserTypeProps) => {
  const { id, damagedId } = useParams();
  const { getCurrentFileOfflineImages, FileImagesList } = useGetOfflineImages();
  const { resetCameraStore } = useImageFlowStore();

  const { data, isFetching, isPending, isSuccess } = useQuery(
    getHasOfflineShootingQueryOptions({
      id: id!,
      damagedId,
      userType,
    }),
  );

  const retakePhoto = () => {
    resetCameraStore();
    retakeImage?.();
  };
  useEffect(() => {
    if (data?.has_offline_shooting) {
      getCurrentFileOfflineImages({ damagedId });
    }
  }, [isSuccess]);
  if (isFetching || isPending) {
    return <LoadingPage />;
  }
  if (data?.has_offline_shooting && FileImagesList?.vehicleImages?.length) {
    return (
      <SendOfflinePhotos retakeImage={retakeImage} damagedId={damagedId!} />
    );
  }
  return (
    <>
      <Flex alignItems="center" justifyContent="space-between">
        <TitleDescription>تصاویر وسیله‌نقلیه</TitleDescription>
        {!!retakeImage ? <RetakeImagesButton onClick={retakePhoto} /> : null}
      </Flex>
      {data?.has_offline_shooting && !FileImagesList?.vehicleImages?.length ? (
        <Box
          w="100%"
          backgroundColor="white"
          p="1rem"
          borderRadius={8}
          m="auto"
          border="1px solid"
          borderColor="orange"
          mt="1rem"
        >
          <Typography textAlign="justify" color="orange" textStyle="b1">
            آخرین تصاویر ثبت شده به صورت آفلاین ذخیره شده و به دلیل تغییر دستگاه
            موبایل یا حذف شدن تصاویر، قابل نمایش نیست. تصاویر نمایش داده شده
            آخرین تصاویر آپلود شده میباشد.
          </Typography>
        </Box>
      ) : null}
      <Box mt={24}>
        <DashedTitle title="تصاویر دور تا دور وسیله‌نقلیه" />
      </Box>
      {images?.vehicleImages.length ? (
        <Flex
          gap={16}
          mt={16}
          flexDir="row"
          flexWrap="wrap"
          className={grid({ columns: 2 })}
        >
          {images?.vehicleImages?.map((image, index) => (
            <Box
              key={image.name}
              minW={146}
              flex={
                index === images?.damagedImages.length - 1 &&
                images?.damagedImages.length % 2 === 0
                  ? 'unset'
                  : 1
              }
            >
              <ImageCard imageData={image} />
            </Box>
          ))}
        </Flex>
      ) : (
        <Box mt={16} mb={16}>
          <VehicleImagesEmptySpace />
        </Box>
      )}

      <>
        <Box mt={24}>
          <Flex alignItems="center" justifyContent="space-between">
            <DashedTitle title="تصاویر نقاط آسیب‌دیده" />
            {addDamagePoint ? (
              <Button variant="text" p={0} onClick={addDamagePoint}>
                <Flex gap={5} alignItems="center">
                  <Typography color="purple-2" textStyle="b4">
                    افزودن نقطه جدید
                  </Typography>
                  <AddIcon width={16} />
                </Flex>
              </Button>
            ) : null}
          </Flex>
          {images?.damagedImages?.length ? (
            <Flex
              gap={16}
              mt={16}
              flexDir="row"
              flexWrap="wrap"
              className={grid({ columns: 2 })}
            >
              {images?.damagedImages?.map((image, index) => (
                <Box
                  key={image.name}
                  minW={146}
                  flex={
                    index === images?.damagedImages.length - 1 &&
                    images?.damagedImages.length % 2 === 0
                      ? 'unset'
                      : 1
                  }
                >
                  <ImageCard
                    imageData={image}
                    extension={
                      handleDeleteImage
                        ? {
                            type: 'delete',
                            onDeletePhotoHandler: (closeModal) => {
                              handleDeleteImage(image?.id || 0, closeModal);
                            },
                          }
                        : undefined
                    }
                  />
                </Box>
              ))}
            </Flex>
          ) : (
            <Box mt={16} mb={16}>
              <DamagePartEmptySpace />
            </Box>
          )}
        </Box>
      </>
    </>
  );
};

export default PreviewImages;

const RetakeImagesButton = ({ onClick }: { onClick: () => void }) => (
  <Button variant="text" p={0} onClick={onClick}>
    <Flex gap={5} alignItems="center">
      <Typography color="purple-2" textStyle="b4">
        عکس برداری مجدد
      </Typography>
      <RefreshIcon color={token('colors.purple-2')} width={14} height={14} />
    </Flex>
  </Button>
);
